import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  message,
  Tooltip,
  Space,
  Tag,
  Typography,
  Popconfirm,
  Alert,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  WarningOutlined,
  CloseCircleFilled,
  DisconnectOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../../css/modal-list-sync.css"; // Asegúrate de que los estilos estén correctamente definidos

const { Text } = Typography;

// Interfaz para los elementos en cola
interface QueuedItem {
  id: string;
  url: string;
  timestamp: number;
  status: string;
  attempts: number;
  method: string;
  data?: any;
}

export const SyncQueueManager: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSync?: () => void;
}> = ({ visible, onClose, onSync }) => {
  const [queuedItems, setQueuedItems] = useState<QueuedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItem, setSelectedItem] = useState<QueuedItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Manejar cambios en el estado de conexión
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Escuchar mensajes del Service Worker
  // Escuchar mensajes del Service Worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "QUEUE_ITEMS") {
        const items = event.data.items.map(normalizeQueueItem);
        setQueuedItems(items);
        setLoading(false);
        if (items.length === 0 && !errorMessage) {
          setErrorMessage("No hay elementos en la cola de sincronización.");
        }
      } else if (event.data && event.data.type === "QUEUE_ERROR") {
        setErrorMessage(event.data.message || "Error al obtener la cola.");
        setQueuedItems([]);
        setLoading(false);
      }
      // ✅ NUEVO: Manejar errores de sincronización
      else if (event.data && event.data.type === "SYNC_ERROR") {
        setLoading(false);

        if (event.data.noConnection) {
          // Error de conectividad con el servidor
          message.error({
            content:
              "No se pudo conectar con el servidor. El servidor no respondió en 3 segundos.",
            duration: 5,
          });
        } else {
          // Otros errores
          message.error({
            content: event.data.error || "Error durante la sincronización",
            duration: 5,
          });
        }
      }
      // ✅ NUEVO: Confirmar sincronización exitosa
      else if (event.data && event.data.type === "SYNC_COMPLETED") {
        setLoading(false);
        message.success({
          content: event.data.message || "Sincronización completada con éxito",
          duration: 4,
        });
        // Recargar la lista después de sincronizar
        setTimeout(() => loadQueuedItems(), 1000);
      }
      // ✅ NUEVO: Notificación de duplicados
      else if (event.data && event.data.type === "DUPLICATE_DETECTED") {
        message.warning({
          content: event.data.message || "Contenedor duplicado detectado",
          duration: 5,
        });
        // Recargar la lista para remover el duplicado
        setTimeout(() => loadQueuedItems(), 1000);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [errorMessage]);

  // Cargar elementos de la cola al abrir el modal
  useEffect(() => {
    if (visible) {
      loadQueuedItems();
    }
  }, [visible]);

  const loadQueuedItems = async () => {
    setLoading(true);
    setErrorMessage(null);

    // Intentar obtener la cola desde el Service Worker primero
    if (navigator.serviceWorker.controller) {
      // AQUÍ ESTÁ EL CAMBIO: Usar MessageChannel para comunicación bidireccional
      const messageChannel = new MessageChannel();

      // Configurar el manejador para recibir respuestas del Service Worker
      messageChannel.port1.onmessage = (event) => {
        if (event.data && event.data.type === "QUEUE_ITEMS") {
          const items = event.data.items.map(normalizeQueueItem);
          setQueuedItems(items);

          if (items.length === 0) {
            setErrorMessage("No hay elementos en la cola de sincronización.");
          }

          setLoading(false);
        } else if (event.data && event.data.type === "QUEUE_ERROR") {
          setErrorMessage(event.data.message || "Error al obtener la cola.");
          setQueuedItems([]);
          setLoading(false);

          // Intentar fallback a IndexedDB si falla la comunicación con el SW
          getQueuedItems()
            .then((items) => {
              setQueuedItems(items);
              if (items.length === 0) {
                setErrorMessage(
                  "No hay elementos en la cola de sincronización."
                );
              }
            })
            .catch((error) => {
              console.error("Error en fallback:", error);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      };

      // Enviar mensaje al Service Worker con el puerto de MessageChannel
      navigator.serviceWorker.controller.postMessage(
        { type: "GET_QUEUE_ITEMS" },
        [messageChannel.port2]
      );
    } else {
      // Fallback a IndexedDB si el Service Worker no está disponible
      try {
        const items = await getQueuedItems();
        setQueuedItems(items);
        if (items.length === 0) {
          setErrorMessage("No hay elementos en la cola de sincronización.");
        }
      } catch (error) {
        console.error("Error al cargar elementos en cola:", error);
        setErrorMessage(
          "No se pudieron cargar los elementos en cola: " +
            (error instanceof Error ? error.message : "Error desconocido")
        );
        setQueuedItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Función para normalizar la estructura de los elementos de la cola
  const normalizeQueueItem = (item: any): QueuedItem => {
    let url =
      item.queueName ||
      item.url ||
      (item.request && item.request.url) ||
      "URL no disponible";
    let method = item.request?.method || "POST";
    let data = null;

    if (item.request?.body) {
      try {
        // Workbox almacena el body como ReadableStream, necesitamos leerlo
        if (item.request.body instanceof ReadableStream) {
          // Nota: Leer ReadableStream en el frontend es complejo, asumimos que el Service Worker lo convierte
          data = item.request.body;
        } else {
          data = JSON.parse(item.request.body);
        }
      } catch (e) {
        data = item.request.body || null;
      }
    }

    return {
      id:
        item.id ||
        `id-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      url,
      timestamp: item.timestamp || Date.now(),
      status: item.status || "pending",
      attempts: item.attempts || 0,
      method,
      data,
    };
  };

  // Función para obtener elementos de IndexedDB
  const getQueuedItems = async (): Promise<QueuedItem[]> => {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        console.warn("IndexedDB no está disponible en este navegador");
        resolve([]);
        return;
      }

      const dbName = "workbox-background-sync";
      const storeName = "wf-cg-container-post-offline";

      console.log(`Abriendo IndexedDB: ${dbName}/${storeName}`);

      const request = indexedDB.open(dbName);

      request.onerror = (event) => {
        console.error(`Error al abrir ${dbName}:`, event);
        reject(new Error(`No se pudo abrir la base de datos: ${event}`));
      };

      request.onsuccess = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          console.warn(
            `Almacén ${storeName} no encontrado, intentando 'requests'`
          );
          if (db.objectStoreNames.contains("requests")) {
            getItemsFromStore(db, "requests", resolve);
          } else {
            db.close();
            resolve([]);
          }
          return;
        }

        getItemsFromStore(db, storeName, resolve);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id" });
        }
      };
    });
  };

  const getItemsFromStore = (
    db: IDBDatabase,
    storeName: string,
    resolve: (items: QueuedItem[]) => void
  ) => {
    try {
      const transaction = db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const items = getAllRequest.result || [];
        console.log(`Encontrados ${items.length} elementos en ${storeName}`);
        resolve(items.map(normalizeQueueItem));
      };

      getAllRequest.onerror = (event) => {
        console.error(`Error al obtener elementos de ${storeName}:`, event);
        resolve([]);
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = (event) => {
        console.error(`Error en transacción con ${storeName}:`, event);
        db.close();
        resolve([]);
      };
    } catch (error) {
      console.error(`Error al acceder a ${storeName}:`, error);
      db.close();
      resolve([]);
    }
  };

  // Intentar reenviar un elemento específico
  const handleRetryItem = async (id: string) => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!isOnline) {
      message.warning(
        "No hay conexión a internet. Intenta nuevamente cuando estés online."
      );
      return;
    }

    setLoading(true);
    const loadingMsg = message.loading(
      "Verificando conexión y reenviando...",
      0
    );

    try {
      navigator.serviceWorker.controller.postMessage({
        type: "RETRY_SPECIFIC_ITEM",
        id,
      });

      // El SW manejará la respuesta con SYNC_COMPLETED o SYNC_ERROR
      // Esperar un momento antes de recargar
      setTimeout(() => {
        loadingMsg();
        loadQueuedItems();
        if (onSync) onSync();
      }, 3000);
    } catch (error) {
      console.error("Error al reenviar elemento:", error);
      loadingMsg();
      message.error("Error al reenviar la solicitud");
      setLoading(false);
    }
  };

  // Eliminar un elemento de la cola
  const handleDeleteItem = async (id: string) => {
    setLoading(true);
    try {
      await deleteQueuedItem(id);
      message.success("Elemento eliminado de la cola");
      loadQueuedItems();
      if (onSync) onSync();
    } catch (error) {
      console.error("Error al eliminar elemento:", error);
      message.error("No se pudo eliminar el elemento");
    } finally {
      setLoading(false);
    }
  };

  const deleteQueuedItem = async (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const dbName = "workbox-background-sync";
      const storeName = "wf-cg-container-post-offline";

      const request = indexedDB.open(dbName);

      request.onerror = (event) => {
        console.error(`Error al abrir ${dbName}:`, event);
        reject(new Error(`No se pudo abrir la base de datos`));
      };

      request.onsuccess = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          if (db.objectStoreNames.contains("requests")) {
            deleteItemFromStore(db, "requests", id, resolve, reject);
          } else {
            db.close();
            reject(new Error(`Almacén ${storeName} no encontrado`));
          }
          return;
        }
        deleteItemFromStore(db, storeName, id, resolve, reject);
      };
    });
  };

  const deleteItemFromStore = (
    db: IDBDatabase,
    storeName: string,
    id: string,
    resolve: () => void,
    reject: (error: Error) => void
  ) => {
    try {
      const transaction = db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        db.close();
        resolve();
      };

      deleteRequest.onerror = (event) => {
        console.error(`Error al eliminar de ${storeName}:`, event);
        db.close();
        reject(new Error(`Error al eliminar elemento`));
      };

      transaction.oncomplete = () => db.close();
      transaction.onerror = (event) => {
        console.error(`Error en transacción de eliminación:`, event);
        db.close();
        reject(new Error(`Error en transacción`));
      };
    } catch (error) {
      console.error(`Error al acceder a ${storeName}:`, error);
      db.close();
      reject(new Error(`Error inesperado al eliminar`));
    }
  };

  const showItemDetails = (item: QueuedItem) => {
    setSelectedItem(item);
    setDetailsVisible(true);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text: string) => (
        <Text ellipsis style={{ maxWidth: 120 }} title={text}>
          {text.substring(0, 8)}...
        </Text>
      ),
      responsive: ["md"],
    },
    {
      title: "Endpoint",
      dataIndex: "url",
      key: "url",
      render: (url: string) => {
        try {
          if (
            url.includes("/workflow-container") ||
            url.includes("/api/workflow")
          ) {
            const parts = url.split("/").filter((part) => part.trim() !== "");
            const endpoint = parts[parts.length - 1] || "desconocido";
            const endpointMap: Record<string, string> = {
              "workflow-container-checkpoint": "Checkpoint",
              "workflow-container-empty": "Container Vacío",
              "workflow-container-videos": "Videos",
              "workflow-container-full": "Container Completo",
              "workflow-truck": "Camión",
            };
            const colorMap: Record<string, string> = {
              checkpoint: "blue",
              empty: "orange",
              videos: "purple",
              full: "green",
              truck: "cyan",
            };
            let displayName = endpoint;
            let color = "default";
            for (const [key, value] of Object.entries(endpointMap)) {
              if (endpoint.includes(key)) {
                displayName = value;
                for (const [typeKey, typeColor] of Object.entries(colorMap)) {
                  if (key.includes(typeKey)) {
                    color = typeColor;
                    break;
                  }
                }
                break;
              }
            }
            return (
              <Tooltip title={url}>
                <Tag color={color}>{displayName}</Tag>
              </Tooltip>
            );
          }
          const parts = url.split("/").filter((part) => part.trim() !== "");
          const endpoint = parts[parts.length - 1] || "desconocido";
          return (
            <Tooltip title={url}>
              <Text>{endpoint}</Text>
            </Tooltip>
          );
        } catch (error) {
          console.error("Error al procesar URL:", error);
          return <Text>Error en URL</Text>;
        }
      },
    },
    {
      title: "Fecha",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: number) => (
        <Tooltip title={dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss")}>
          <Text>{dayjs(timestamp).format("DD/MM/YYYY HH:mm")}</Text>
        </Tooltip>
      ),
      responsive: ["sm"],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: QueuedItem) => {
        let color = "blue";
        let text = "Pendiente";
        let icon = null;
        switch (status) {
          case "pending":
            color = "blue";
            text = "Pendiente";
            icon = <ClockCircleOutlined />;
            break;
          case "in_progress":
            color = "orange";
            text = "En proceso";
            icon = <SyncOutlined spin />;
            break;
          case "failed":
            color = "red";
            text = `Fallido (${record.attempts || 0})`;
            icon = <WarningOutlined />;
            break;
          case "completed":
            color = "green";
            text = "Completado";
            icon = <InfoCircleOutlined />;
            break;
          default:
            color = "default";
            text = status || "Desconocido";
        }
        return (
          <Tag color={color} icon={icon}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Método",
      dataIndex: "method",
      key: "method",
      render: (method: string) => <Tag color="purple">{method}</Tag>,
      responsive: ["lg"],
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: QueuedItem) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              onClick={() => showItemDetails(record)}
            />
          </Tooltip>
          <Tooltip
            title={isOnline ? "Reintentar" : "Necesitas conexión a internet"}
          >
            <Button
              icon={<ReloadOutlined />}
              size="small"
              type="primary"
              disabled={!isOnline}
              onClick={() => handleRetryItem(record.id)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Eliminar solicitud?"
              description="Esta solicitud se eliminará permanentemente"
              onConfirm={() => handleDeleteItem(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ] as any[];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;

    setLoading(true);
    try {
      for (const key of selectedRowKeys) {
        await deleteQueuedItem(key.toString());
      }
      message.success(`${selectedRowKeys.length} elementos eliminados`);
      setSelectedRowKeys([]);
      loadQueuedItems();
      if (onSync) onSync();
    } catch (error) {
      console.error("Error al eliminar elementos:", error);
      message.error("Error al eliminar algunos elementos");
    } finally {
      setLoading(false);
    }
  };

  const handleProcessAll = () => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!isOnline) {
      message.warning(
        "No hay conexión a internet. Necesitas estar online para sincronizar."
      );
      return;
    }

    // ✅ Mostrar indicador de carga
    setLoading(true);

    const loadingMessage = message.loading(
      "Verificando conexión con el servidor...",
      0
    );

    // Enviar solicitud de sincronización al SW
    navigator.serviceWorker.controller.postMessage({
      type: "SYNC_NOW",
    });

    // El SW responderá con SYNC_COMPLETED, SYNC_ERROR, o DUPLICATE_DETECTED
    // Por si acaso, cerrar el loading después de un timeout
    setTimeout(() => {
      loadingMessage();
      setLoading(false);
    }, 10000); // 10 segundos máximo
  };

  return (
    <>
      <Modal
        title={
          <div>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            Gestor de Cola de Sincronización
            {queuedItems.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {queuedItems.length} elemento(s)
              </Tag>
            )}
            <Tag
              color={isOnline ? "green" : "red"}
              icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
              style={{ marginLeft: 8 }}
            >
              {isOnline ? "Online" : "Offline"}
            </Tag>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width="90vw"
        footer={[
          <Button
            key="refresh"
            onClick={loadQueuedItems}
            icon={<ReloadOutlined />}
          >
            Refrescar
          </Button>,
          <Button
            key="process-all"
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleProcessAll}
            disabled={!isOnline || queuedItems.length === 0}
          >
            Procesar Todo
          </Button>,
          <Button
            key="delete-selected"
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            disabled={selectedRowKeys.length === 0}
          >
            Eliminar ({selectedRowKeys.length})
          </Button>,
          <Button key="close" onClick={onClose}>
            Cerrar
          </Button>,
        ]}
      >
        {!isOnline && (
          <Alert
            message="Modo offline activo"
            description="No hay conexión a internet. Puedes ver y eliminar solicitudes pendientes, pero la sincronización requiere conexión."
            type="warning"
            showIcon
            icon={<DisconnectOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {errorMessage && (
          <Alert
            message="Información"
            description={errorMessage}
            type={queuedItems.length === 0 ? "info" : "error"}
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={queuedItems.map((item) => ({ ...item, key: item.id }))}
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  loading
                    ? "Cargando elementos..."
                    : errorMessage ||
                      "No hay elementos en la cola de sincronización"
                }
              />
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </Modal>

      <Modal
        title={
          <div>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Detalles de la Solicitud
            {selectedItem?.method && (
              <Tag color="purple" style={{ marginLeft: 8 }}>
                {selectedItem.method}
              </Tag>
            )}
          </div>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Cerrar
          </Button>,
        ]}
        width={700}
      >
        {selectedItem && (
          <div>
            <h3>Información General</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <div>
                <strong>ID:</strong> {selectedItem.id}
              </div>
              <div>
                <strong>Estado:</strong>{" "}
                <Tag
                  color={
                    selectedItem.status === "pending"
                      ? "blue"
                      : selectedItem.status === "in_progress"
                      ? "orange"
                      : selectedItem.status === "failed"
                      ? "red"
                      : selectedItem.status === "completed"
                      ? "green"
                      : "default"
                  }
                >
                  {selectedItem.status === "pending"
                    ? "Pendiente"
                    : selectedItem.status === "in_progress"
                    ? "En Progreso"
                    : selectedItem.status === "failed"
                    ? "Fallido"
                    : selectedItem.status === "completed"
                    ? "Completado"
                    : selectedItem.status}
                </Tag>
              </div>
              <div>
                <strong>Fecha:</strong>{" "}
                {selectedItem.timestamp
                  ? dayjs(selectedItem.timestamp).format("DD/MM/YYYY HH:mm:ss")
                  : "Desconocida"}
              </div>
              <div>
                <strong>Intentos:</strong> {selectedItem.attempts || 0}
              </div>
            </div>

            <h3 style={{ marginTop: 16 }}>Información de Endpoint</h3>
            <div>
              <strong>URL Completa:</strong> {selectedItem.url}
            </div>
            {selectedItem.url.includes("workflow-container") && (
              <div style={{ marginTop: 8 }}>
                <strong>Tipo de Flujo:</strong>{" "}
                {selectedItem.url.includes("checkpoint") ? (
                  <Tag color="blue">Checkpoint</Tag>
                ) : selectedItem.url.includes("empty") ? (
                  <Tag color="orange">Container Vacío</Tag>
                ) : selectedItem.url.includes("videos") ? (
                  <Tag color="purple">Videos</Tag>
                ) : selectedItem.url.includes("full") ? (
                  <Tag color="green">Container Completo</Tag>
                ) : selectedItem.url.includes("truck") ? (
                  <Tag color="cyan">Camión</Tag>
                ) : (
                  <Tag>Desconocido</Tag>
                )}
              </div>
            )}

            {selectedItem.data && (
              <>
                <h3 style={{ marginTop: 16 }}>Datos de la Solicitud</h3>
                {typeof selectedItem.data === "object" &&
                  selectedItem.data !== null && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      {selectedItem.data.containerId && (
                        <div>
                          <strong>ID de Contenedor:</strong>{" "}
                          {selectedItem.data.containerId}
                        </div>
                      )}
                      {selectedItem.data.truckId && (
                        <div>
                          <strong>ID de Camión:</strong>{" "}
                          {selectedItem.data.truckId}
                        </div>
                      )}
                      {selectedItem.data.checkpointId && (
                        <div>
                          <strong>ID de Checkpoint:</strong>{" "}
                          {selectedItem.data.checkpointId}
                        </div>
                      )}
                      {selectedItem.data.clientId && (
                        <div>
                          <strong>ID de Cliente:</strong>{" "}
                          {selectedItem.data.clientId}
                        </div>
                      )}
                    </div>
                  )}
                <div style={{ marginTop: 16 }}>
                  <strong>Datos Completos:</strong>
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: 16,
                      borderRadius: 4,
                    }}
                  >
                    {typeof selectedItem.data === "object"
                      ? JSON.stringify(selectedItem.data, null, 2)
                      : String(selectedItem.data)}
                  </pre>
                </div>
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  type="primary"
                  disabled={!isOnline}
                  onClick={() => {
                    handleRetryItem(selectedItem.id);
                    setDetailsVisible(false);
                  }}
                >
                  Reintentar Envío
                </Button>
                <Popconfirm
                  title="¿Eliminar solicitud?"
                  description="Esta solicitud se eliminará permanentemente"
                  onConfirm={() => {
                    handleDeleteItem(selectedItem.id);
                    setDetailsVisible(false);
                  }}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger>
                    Eliminar
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export const SyncQueueButton: React.FC<{
  pendingCount?: number;
}> = ({ pendingCount = 0 }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <>
      <Button
        type="primary"
        icon={<ClockCircleOutlined />}
        onClick={() => setModalVisible(true)}
      >
        Cola de Sincronización
        {pendingCount > 0 && (
          <Tag color="red" style={{ marginLeft: 8 }}>
            {pendingCount}
          </Tag>
        )}
        {!isOnline && (
          <Tooltip title="Sin conexión a internet">
            <DisconnectOutlined style={{ marginLeft: 5, color: "#ff4d4f" }} />
          </Tooltip>
        )}
      </Button>

      {modalVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de cola"
              open={true}
              onCancel={() => setModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setModalVisible(false)}>
                  Cerrar
                </Button>,
              ]}
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de cola. Esto puede deberse a problemas con la base de datos local o la conexión."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <SyncQueueManager
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback: React.ReactNode;
}> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Error en componente:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
